import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import ApiKeyInfoModal from "./components/ApiKeyInfoModal/index";
import ApiKeyConfirmationModal from "./components/ApiKeyConfirmationModal/index";

import { API_ENV, API_HOST, ADMIN_API_KEY } from "../../../constants";

import "./styles/index.css";

const getApiKeys = async (apiEnv = API_ENV.DEV) => {
  const response = await axios.get(
    `${API_HOST[apiEnv]}/v1/keys`,
    {
      headers: { "X-API-KEY": ADMIN_API_KEY[apiEnv] },
    }
  );

  return response.data;
};

const deleteApiKey = async (apiKeyId, apiEnv) => {
  const response = await axios.delete(
    `${API_HOST[apiEnv]}/v1/keys/${apiKeyId}`,
    {
      headers: { "X-API-KEY": ADMIN_API_KEY[apiEnv] },
    }
  );

  return response;
};

const createApiKey = async (apiEnv, name) => {
  const body = { name };

  const response = await axios.post(
    `${API_HOST[apiEnv]}/v1/keys`,
    body,
    {
      headers: { "X-API-KEY": ADMIN_API_KEY[apiEnv] },
    }
  );

  return response;
};

const editApiKey = async (apiEnv, params) => {
  const body = {
    name: params.name,
    callbackUrl: params.callbackUrl,
    enabled: params.enabled,
  };

  const response = await axios.patch(
    `${API_HOST[apiEnv]}/v1/keys/${params.id}`,
    body,
    {
      headers: { "X-API-KEY": ADMIN_API_KEY[apiEnv] },
    }
  );

  return response;
};

const useGetApiKeys = ({
  apiEnv,
}) => {
  const { data, isError, isLoading, refetch } = useQuery({
    queryKey: ["getApiKeys", apiEnv],
    queryFn: async () => await getApiKeys(apiEnv),
    gcTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    apiKeys: data?.data ?? [],
    refetchApiKeys: refetch,
    isError,
    isLoading,
  };
};

const ApiKey = ({ apiKey, onClick }) => (
  <div
    className={`api-card ${apiKey.enabled ? "enabled" : "disabled"}`}
    onClick={() => onClick(apiKey)}
  >
    <h4>{apiKey.name}</h4>

    <p>{apiKey.enabled ? "Enabled" : "Disabled"}</p>
  </div>
);

const ApiKeysPage = () => {
  const [selectedApiKey, setSelectedApiKey] = useState(null);
  const [selectedForDeletion, setSelectedForDeletion] = useState(null);
  const { environment } = useSelector((state) => state.config);

  const { apiKeys, refetchApiKeys, isError, isLoading } = useGetApiKeys({ apiEnv: environment });

  const [createApiKeyModalOpen, setCreateApiKeyModalOpen] = useState(false);

  const closeEditModal = () => setSelectedApiKey(null);
  const closeCreateModal = () => setCreateApiKeyModalOpen(false);

  const handleCreateClick = (name) => {
    closeCreateModal();
    createApiKey(environment, name)
      .then((response) => {
        if (response.status !== 201) {
          // TODO: Implement an error Toast notification
          console.error("Failed to create API Key");
          return;
        }

        refetchApiKeys();
      });
  }

  const handleEditClick = (params) => {
    closeEditModal();
    editApiKey(environment, params)
      .then((response) => {
        if (response.status !== 200) {
          // TODO: Implement an error Toast notification
          console.error("Failed to edit API Key");
          return;
        }

        refetchApiKeys();
      });
  }

  const handleDeleteClick = (apiKey) => setSelectedForDeletion(apiKey);

  const handleDeleteConfirmationClick = (apiKeyId) => {
    closeEditModal();
    deleteApiKey(apiKeyId, environment)
      .then((response) => {
        if (response.status !== 200) {
          // TODO: Implement an error Toast notification
          console.error("Failed to delete API Key");
          return;
        }
    
        refetchApiKeys();
      });
  };

  return (
    <section className="page">
      <div className="api-keys-header">
        <h1>{environment} API Keys</h1>

        <button
          className="btn primary btn--small"
          onClick={() => setCreateApiKeyModalOpen(true)}
          id="api-key-create-button"
        >
          {'Create'}{' '}<span>{'new API Key'}</span>
        </button>
      </div>

      {isLoading && <p>Loading...</p>}
      {isError && <p>Error loading API Keys</p>}
      {!apiKeys.length && !isLoading && !isError && <p>No API Keys found</p>}

      {apiKeys
        .sort((a, b) => new Date(b.lastUpdatedDate) - new Date(a.lastUpdatedDate))
        .map((apiKey) =>
          <ApiKey apiKey={apiKey} key={apiKey.id} onClick={() => setSelectedApiKey(apiKey)} />
        )
      }

      {createApiKeyModalOpen &&
        <ApiKeyInfoModal
          isCreate
          onOutsideClick={() => setCreateApiKeyModalOpen(false)}
          onCreateClick={handleCreateClick}
          onCancelClick={() => setCreateApiKeyModalOpen(false)}
        />
      }

      {selectedApiKey &&
        <ApiKeyInfoModal
          isEdit
          onOutsideClick={() => setSelectedApiKey(null)}
          onDeleteClick={handleDeleteClick}
          onEditClick={handleEditClick}
          onCancelClick={() => setSelectedApiKey(null)}
          apiKey={selectedApiKey}
        />
      }

      <ApiKeyConfirmationModal
        open={!!selectedForDeletion}
        onOutsideClick={() => setSelectedForDeletion(null)}
        onDeleteClick={handleDeleteConfirmationClick}
        onCancelClick={() => setSelectedForDeletion(null)}
        apiKeyId={selectedForDeletion?.id}
        apiKeyName={selectedForDeletion?.name}
      />
    </section>
  )
};

export default ApiKeysPage;
